import * as React from 'react';
import { LIST_ROW_HEIGHTS } from '../../../constants/list_row_heights';
import { IScoreboardSettings, ITest } from '../../../data/types';
import { setScoreboardSettings } from '../../../data/writers/settings/scoreboardSettings';
import { useEntities } from '../../../hooks/useEntities';
import { Column } from '../../layout/Column';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { EntityFilter } from '../../shared/filters/EntityFilter';
import { List } from '../../shared/List/List';
import { NoResults } from '../../shared/NoResults';
import { ScoreboardItem } from './ScoreboardItem';

interface IProps {
  data: ITest[];
  selectTest: (id: ITest) => void;
  settings: IScoreboardSettings;
}

export const ScoreboardList = (props: IProps): React.ReactElement => {
  const { teams, groups, loading } = useEntities();
  const metric = props.settings?.metrics?.[props.settings?.testType?.value];
  const filterChangeHandler = async (
    filteredTeams: string[],
    filteredGroups: string[]
  ): Promise<void> => {
    await setScoreboardSettings({
      ...props.settings,
      filteredTeams,
      filteredGroups,
    });
  };

  const rowRenderer = ({
    index,
    key,
    style,
  }: {
    index: number;
    key: string;
    style: React.CSSProperties;
  }): React.ReactElement => {
    const row = props.data[index];
    const clickHandler = (): void => props.selectTest(row);

    return (
      <ScoreboardItem
        style={style}
        key={key}
        test={row}
        metric={metric}
        goToTest={clickHandler}
        testType={props.settings?.testType}
        index={index}
      />
    );
  };

  return (
    <Card
      style={{
        flex: 1,
        marginTop: 'var(--grid-gap)',
        marginRight: 'var(--grid-gap)',
        height: 'calc(100% - var(--grid-gap) * 2)',
      }}
    >
      <Column>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <Column>
              <div>{props.settings?.testType.label}</div>
              <div>{metric.label}</div>
            </Column>
          </CardHeaderLeft>
          <CardHeaderRight>
            <EntityFilter
              level={1}
              teams={teams}
              groups={groups}
              teamsLoading={loading}
              groupsLoading={loading}
              filteredTeams={props.settings?.filteredTeams}
              filteredGroups={props.settings?.filteredGroups}
              applyTeamFilter={(): void => undefined}
              applyFilters={filterChangeHandler}
            />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {props.data.length === 0 ? (
            <NoResults message='No tests found with the current settings/filters' />
          ) : (
            <List
              rowHeight={LIST_ROW_HEIGHTS.large}
              rowRenderer={rowRenderer}
              listLength={props.data.length}
            />
          )}
        </CardBody>
      </Column>
    </Card>
  );
};
